import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { useSelector, useDispatch } from 'react-redux'
import styled from 'styled-components/macro'
import SearchResultItem from 'components/ui/SearchResultItem'
import { t } from 'ttag'
import SearchResultTitle from 'components/ui/SearchResultTitle'
import SearchResultListing from 'components/ui/SearchResultListing'
import SearchResultContent from 'components/ui/SearchResultContent'
import ExtendedSearchResultInfo from 'components/ui/ExtendedSearchResultInfo'
import InfoCol from 'components/ui/InfoCol'
import SearchResultSelectedFilters from 'components/ui/SearchResultSelectedFilters'
import FilterSelection from 'components/FilterSelection'
import ResultInfo from 'components/ui/ResultInfo'
import CustomPagination from 'components/CustomPagination'
import SearchFooter from 'components/ui/SearchFooter'
import ResultActions from 'components/ui/ResultActions'
import ResultAction from 'components/ui/ResultAction'
import ResultActionIcon from 'components/ui/ResultActionIcon'
import ResultUrlContainer from 'components/ui/ResultUrlContainer'
import LoadingSpinner from 'components/ui/LoadingSpinner'
import { getResultUrl } from 'util/hash'
import { PROGRAMMES_RESULTS_PER_PAGE } from 'store/constants/constants'
import SpinnerContainer from 'components/ui/SpinnerContainer'
import { show } from 'redux-modal'
import ChangeOrder from 'components/ChangeOrder'

const Link = styled.a`
  color: #003399;
  display: block;
  text-decoration: none;
`

const ResultTitle = styled.div`
  color: ${props => (props.disabled ? 'grey' : '#003399')};
  font-weight: bold;
  font-size: 16px;
  padding: 4px 0;
  ${props => props.disabled && 'cursor: not-allowed'}
`
const ProgrammeSearchResultContent = styled(SearchResultContent)`
  padding: 12px;
`
const Loading = styled.div`
  position: relative !important;
  margin-top: 50px;
`
const ErrorText = styled.p`
  color: #f31100;
`
const InfoLink = styled.a`
  margin-left: 10px;
  cursor: pointer;
`

function ProgrammeSearchResults({
  searchResults: { results = [], count, current, next, previous, total_pages: totalPages },
  isPending,
  error,
  isSidebarVisible,
  reduced,
  onPageChange,
  onExportToExcel,
  currentFilters,
  onGetResultUrl,
  resultHash,
  isExcelExportPending,
  orderby,
  orderChanged,
  selectedFilters
}) {
  const dispatch = useDispatch()
  const filters = useSelector(state => state.filters)
  const { currentSearchable } = filters
  const [state, setState] = useState({ orderby: orderby })
  const [sectionsContainer, setSectionsContainer] = useState(
    document.getElementById('summary-options')
  )
  const [filterLength, setFilterLength] = useState(0)
  useEffect(() => {
    const updateSectionsContainer = () => {
      const element = document.getElementById('summary-options')
      countFilters(element)
      setSectionsContainer(element)
    }
    updateSectionsContainer()
  }, [])

  const countFilters = filterSections => {
    return new Promise(resolve => {
      setTimeout(() => {
        const filterLength = filterSections
          ? filterSections.querySelectorAll('.filtering').length
          : 0
        const col = filterSections ? filterSections.querySelectorAll('.summary') : [null]
        setFilterLength(filterLength, col[0])
        if (col[0]) {
          if (filterLength < 1) {
            col[0].classList.add('hide')
          } else {
            col[0].classList.add('show')
          }
        }
        resolve(filterLength)
      }, 10)
    })
  }

  useEffect(() => {
    const filterSections = document.querySelector('.filter-sections')
    countFilters(filterSections).then(length => {
      setFilterLength(length) // Update state with the filter length
    })
  }, [sectionsContainer])

  const updateOrder = i => {
    orderChanged(i)
    setState({
      orderby: i
    })
  }

  let resultsComponent
  if (results.length === 0) {
    resultsComponent = <div className="no-results">{t`No results found`}</div>
  } else {
    resultsComponent = (
      <>
        <ChangeOrder
          orderby={orderby === '' ? '' : state.orderby}
          orderChanged={i => updateOrder(i)}
          type="programmes"
        />
        <SearchResultListing>
          {results.map(result => (
            <SearchResultItem key={result.id} className="list-programme">
              <ProgrammeSearchResultContent>
                {result.disabled ? (
                  <ResultTitle disabled={true} className="item-shaded programme">
                    {result.title}
                  </ResultTitle>
                ) : (
                  <Link
                    target="_blank"
                    href={`/programmes/${result.id}/`}
                    className="item-shaded programme"
                  >
                    <ResultTitle>{result.title}</ResultTitle>
                    <div className="read-more">
                      <i className="fa fa-chevron-right"></i>
                    </div>
                  </Link>
                )}
              </ProgrammeSearchResultContent>
            </SearchResultItem>
          ))}
        </SearchResultListing>
        {count > PROGRAMMES_RESULTS_PER_PAGE && (
          <SearchFooter>
            <CustomPagination
              onPageChange={data => onPageChange(data, false, orderby === '' ? '' : state.orderby)}
              count={count}
              current={current}
              next={next}
              previous={previous}
              totalPages={totalPages}
              section={'programmes'}
            />
          </SearchFooter>
        )}
      </>
    )
  }

  if (error) {
    resultsComponent = <ErrorText>{t`An unexpected error occurred.`}</ErrorText>
  }

  return (
    <>
      <SearchResultTitle>
        <h1>
          {currentSearchable === 'countries_and_regions' ? null : t`Search results: Programmes`}
        </h1>
      </SearchResultTitle>
      <div className="fixed-buttons">
        <ResultAction onClick={onExportToExcel} id="prog_to_excel">
          {isExcelExportPending ? (
            <SpinnerContainer>
              <LoadingSpinner showText={false} />
            </SpinnerContainer>
          ) : (
            <ResultActionIcon className="far fa-file-excel" />
          )}
          <div className="export-texts">
            <span>
              {isExcelExportPending ? 'Exporting ' : 'Export '}
              {t`all programme data from your search results to Excel`}
            </span>
            <span className="export-subtitle">
              {t`Please allow extended time for your export to fully download`}
            </span>
          </div>
        </ResultAction>
      </div>
      <ExtendedSearchResultInfo>
        <ResultActions>
          <>
            {resultHash === '' ? (
              <ResultAction onClick={onGetResultUrl} id="prog_get_result_url">
                {t`Copy page URL`} <ResultActionIcon className="far fa-external-link" />
              </ResultAction>
            ) : (
              <>
                <ResultUrlContainer
                  onClick={e => e.target.select()}
                  defaultValue={getResultUrl(resultHash)}
                />
                <InfoLink
                  target="_blank"
                  onClick={async () => {
                    const data = await fetch(
                      `/wp-json/wp/v2/pages?slug=url-get-results-url-how-to-shorten-the-urls`
                    )
                    const jsonData = await data.json()
                    const msg = jsonData[0].content.rendered
                    dispatch(show('infoModal', { message: msg }))
                  }}
                >{t`How to shorten the URLs?`}</InfoLink>
              </>
            )}
          </>
        </ResultActions>
        <InfoCol>
          <div className="evening full-width">
            <div className="col results-summary">
              {!isPending && <h4 className="moderate">Summary of search results</h4>}
              {!isPending && !error && (
                <ResultInfo>
                  <div className="results-tip">
                    <div className="tipped"><span>{t`${count.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ')}`}</span> {t`programmes`}</div>
                  </div>
                </ResultInfo>
              )}
            </div>
          </div>
          <div className="break"></div>
          {!isPending && (
          <div id="summary-options" className="evening new-line programmes">
            {(isSidebarVisible || reduced) && (
              <SearchResultSelectedFilters
                className={`col summary ${filterLength > 0 ? 'show' : 'hide'}`}
              >
                <h4 id="summary-search-options">
                  Summary of search options{' '}
                  {filterLength > 0 && <span>{'(' + filterLength + ')'}</span>}
                </h4>
                <div className="selected-container">
                  <FilterSelection
                    selectedFilters={selectedFilters?.programmes}
                    currentFilters={currentFilters}
                    onFilterCountChange={setFilterLength}
                  />
                </div>
              </SearchResultSelectedFilters>
            )}
          </div>
          )}
          <p style={{ marginBottom: '0px' }}>
            {t`Export all the data on these programmes to a spreadsheet (button above), or check each programme one by one (entries below).`}
          </p>
          <p>
            {t`To fully grasp the state of play on the information provided by keep.eu on programmes, please refer to the state of play of data on programmes,`}{' '}
            <a
              href="https://keep.eu/faq/data-on-programmes-what-is-the-state-of-play/"
              rel="noreferrer"
              target="_blank"
            >
              here
            </a>
            .
          </p>
        </InfoCol>
      </ExtendedSearchResultInfo>
      {isPending ? (
        <Loading>
          <LoadingSpinner showText />
        </Loading>
      ) : (
        <>{resultsComponent}</>
      )}
    </>
  )
}

ProgrammeSearchResults.propTypes = {
  searchResults: PropTypes.shape({
    results: PropTypes.array,
    count: PropTypes.number,
    current: PropTypes.number,
    next: PropTypes.string,
    previous: PropTypes.string,
    total_pages: PropTypes.number
  }),
  isPending: PropTypes.bool,
  orderby: PropTypes.string,
  orderChanged: PropTypes.func,
  error: PropTypes.bool,
  isSidebarVisible: PropTypes.bool,
  reduced: PropTypes.bool,
  onPageChange: PropTypes.func,
  onExportToExcel: PropTypes.func,
  currentFilters: PropTypes.array,
  onGetResultUrl: PropTypes.func,
  resultHash: PropTypes.string,
  isExcelExportPending: PropTypes.bool,
  selectedFilters: PropTypes.object
}

export default ProgrammeSearchResults
